"use client"

import SmallPublicationRow from "./SmallPublicationRow"
import { notEmpty } from "@/util/query"
import { useState, useEffect } from "react"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { PublicBlogResponseWithAuthorName } from "@/types/responses/public"
import { subscribedToSuggestedPublication } from "../../tracking"
import { PublicUser } from "@/types/users"

type BlogWithAuthors = PublicBlogResponseWithAuthorName & {
  authors: PublicUser[]
}

/**
 * This is a client-side component that takes a list of suggested publications and displays a list of 5 of them.
 *
 * If the user subscribes to a publication, we remove it from the list of suggested publications and pick another one.
 */
export function SuggestedPublicationsPicker({
  suggestedPublications,
  isUserLoggedIn,
}: {
  suggestedPublications: BlogWithAuthors[]
  isUserLoggedIn: boolean
}) {
  // Keep track of all available publications.
  const [availablePublications, setAvailablePublications] = useState<
    BlogWithAuthors[]
  >(suggestedPublications)

  // Initially, we show 5 publications.
  const [selectedPublications, setSelectedPublications] = useState<
    BlogWithAuthors[]
  >(() => {
    return availablePublications.slice(0, 5)
  })

  // If the user subscribes to a publication, we remove it and pick another one.
  const [animatingRowId, setAnimatingRowId] = useState<string | null>(null)
  const [newRowId, setNewRowId] = useState<string | null>(null)

  useEffect(() => {
    if (newRowId) {
      const timer = setTimeout(() => setNewRowId(null), 300) // Reduced from 500ms
      return () => clearTimeout(timer)
    }
  }, [newRowId])

  const handleSubscribe = (blogId: string) => {
    subscribedToSuggestedPublication(blogId)
    setAnimatingRowId(blogId)

    setTimeout(() => {
      setAvailablePublications((prev) => prev.filter((p) => p.id !== blogId))

      setSelectedPublications((prev) => {
        const index = prev.findIndex((p) => p.id === blogId)
        if (index === -1) return prev

        const newPublication = availablePublications.find(
          (p) => !prev.includes(p) && p.id !== blogId
        )

        if (newPublication) {
          const newSelected = [...prev]
          newSelected[index] = newPublication
          setNewRowId(newPublication.id)
          return newSelected
        }

        return prev.filter((p) => p.id !== blogId)
      })

      setAnimatingRowId(null)
    }, 200)
  }

  if (selectedPublications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <CheckCircleIcon className="h-16 w-16 text-green-500 mb-4" />
        <h2 className="text-2xl font-bold mb-2">None left.</h2>
        <p className="text-muted-foreground mt-8">
          Refresh the page if you want more suggestions.
        </p>
      </div>
    )
  }

  return (
    <ul className="space-y-4">
      {selectedPublications.filter(notEmpty).map((item) => (
        <li
          key={item.id}
          className={`transition-all duration-300 ease-out ${
            animatingRowId === item.id
              ? "opacity-0 -translate-y-2"
              : newRowId === item.id
                ? "opacity-0 translate-y-2"
                : "opacity-100 translate-y-0"
          }`}
        >
          <SmallPublicationRow
            blog={item}
            imageUrl={item?.logo_url ?? ""}
            title={item?.name ?? ""}
            subtitle={""}
            url={`https://paragraph.com/${item?.lowercase_url}`}
            authors={item?.authors ?? []}
            isUserSubscribed={false}
            isUserLoggedIn={isUserLoggedIn}
            onSubscribeCallback={() => handleSubscribe(item.id)}
            delay={500}
          />
        </li>
      ))}
    </ul>
  )
}
